// =============================================================================
// usagesModules.js : noter l'ouverture d'un module (F1, « Usages d'Aramis »,
// 11/10/2026). Côté navigateur, rien que ceci : à chaque changement de vue,
// App.jsx demande « dans quel module est-on ? » ; si c'est un des dix modules
// du menu, la CF usageNoterModule (Francfort) en est prévenue, au plus une
// fois par module, par heure et par onglet, sans jamais retenir la navigation.
// Le serveur relit la fiche, sa mutuelle, exclut les comptes techniques et les
// invités, et garde des agrégats (functions/lib/usagesModules.js) et, depuis
// la décision de Pierre du 11/10/2026, le nombre d'ouvertures par module et
// par jour de la personne (functions/lib/usagesPersonnes.js, 12 mois). Rien
// d'ici ne transmet une mutuelle, un nom, ni la vue précise : l'identité est
// celle du jeton, relue au serveur.
// Jamais pour une page publique à jeton (présence, pouvoir, émargement, repas,
// lien de vote, invitation, export…), ni sans session Firebase.
// Ce fichier est dans le bundle principal : il n'importe que navConstants, le
// reste (authGuard, firebase) est chargé à la demande.
// =============================================================================
import { MODULE_VIEWS, NAV_LIBELLES } from "./navConstants";

/** Les dix modules du menu, dans l'ordre du rail (miroir serveur :
 *  functions/lib/usagesModules.js, MODULES_USAGE, tenu par le test). Affaires
 *  publiques (« ap ») depuis le 12/10/2026 : ses pages comptaient avant pour
 *  Activité ; l'historique n'est pas réécrit. */
export const MODULES_USAGE = Object.freeze(Object.keys(NAV_LIBELLES));

/** Ce que les deux écrans « Usages d'Aramis » disent de la coupure : avant la
 *  mise en service du module (12/10/2026), et sur un navigateur qui n'a pas
 *  encore rechargé Aramis, une ouverture d'Affaires publiques est comptée sous
 *  Activité (« athos ») ; rien n'est réécrit. */
export const AVANT_MODULE_AP = "Avant la mise en service du module Affaires publiques (12/10/2026), et sur un navigateur qui n'a pas encore rechargé Aramis, ses pages comptent pour Activité : l'historique n'est pas réécrit.";

/** Le module d'une vue parmi les dix, ou null (Ma semaine, Moi,
 *  Administration, page publique…). Lu dans MODULE_VIEWS, jamais une liste en dur. */
export function moduleUsageDeVue(view) {
  if (typeof view !== "string" || !view) return null;
  return MODULES_USAGE.find((m) => (MODULE_VIEWS[m] || []).includes(view)) || null;
}

/** Une adresse de page publique : chemin propre (/event/…, /co, /vote/…,
 *  /espace-salarie, /repondre…) ou jeton dans l'adresse (?t=, ?token=). */
export function adresseAJeton(loc) {
  try {
    const chemin = (loc && loc.pathname) || "/";
    if (chemin !== "/" && chemin !== "/index.html") return true;
    const p = new URLSearchParams((loc && loc.search) || "");
    return p.has("t") || p.has("token");
  } catch { return true; }
}

/** Compte qui ne compte pas : rien n'est envoyé (le serveur l'exclut aussi). */
const sessionExclue = (s) => !s || !s.id || s.id === "argos-verif" || s.technique === true || s.role === "invite";

/** Le module à noter pour cette vue, ou null. */
export function moduleANoter({ view, session, lienPublic = false, location = typeof window !== "undefined" ? window.location : null }) {
  if (sessionExclue(session) || lienPublic || adresseAJeton(location)) return null;
  return moduleUsageDeVue(view);
}

const FMT_PARIS = typeof Intl !== "undefined" ? new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hourCycle: "h23",
}) : null;
/** « AAAA-MM-JJTHH » à Paris : la case horaire du dédoublonnage. */
export function heureParis(ms) {
  if (!FMT_PARIS) return new Date(ms).toISOString().slice(0, 13);
  const p = {};
  for (const x of FMT_PARIS.formatToParts(new Date(ms))) p[x.type] = x.value;
  return `${p.year}-${p.month}-${p.day}T${String(Number(p.hour) % 24).padStart(2, "0")}`;
}

// Mémoire de l'onglet : sessionStorage (survit au rechargement), doublée d'une
// carte en mémoire (navigation privée, stockage bloqué).
const memoire = new Map();
const CLE = (m) => `aramis_usage_${m}`;
function lireCase(m) {
  try { const v = window.sessionStorage.getItem(CLE(m)); if (v) return v; } catch { /* stockage bloqué */ }
  return memoire.get(m) || null;
}
function ecrireCase(m, v) {
  if (v) memoire.set(m, v); else memoire.delete(m);
  try { if (v) window.sessionStorage.setItem(CLE(m), v); else window.sessionStorage.removeItem(CLE(m)); } catch { /* stockage bloqué */ }
}
/** Pour les tests : oublier ce que l'onglet a déjà noté. */
export function oublierCasesUsage() { MODULES_USAGE.forEach((m) => ecrireCase(m, null)); }

/**
 * Prévient le serveur de l'ouverture d'un module, au plus une fois par module
 * et par heure dans cet onglet. Ne lève jamais ; rend true si l'appel est parti.
 * `deps` (tests) : { charger: () => ({ attendreAuthPrete, signalerSiDefi, auth,
 * appeler(nom, data) }), maintenant }.
 */
export async function noterOuvertureModule(module, session, deps = {}) {
  if (!MODULES_USAGE.includes(module) || sessionExclue(session)) return false;
  const maintenant = deps.maintenant || Date.now;
  const caseHoraire = heureParis(maintenant());
  if (lireCase(module) === caseHoraire) return false;
  ecrireCase(module, caseHoraire);
  let outils;
  try { outils = await (deps.charger || chargerOutils)(); } catch { ecrireCase(module, null); return false; }
  try {
    await outils.attendreAuthPrete();
    // Sans session Firebase, le serveur ne compterait pas (jeton Aramis exigé) :
    // on ne pose pas la question, et la case reste libre pour la vue suivante.
    if (!outils.auth || !outils.auth.currentUser) { ecrireCase(module, null); return false; }
    await outils.appeler("usageNoterModule", { module, aramisUserId: session.id });
    return true;
  } catch (e) {
    try { outils.signalerSiDefi(e); } catch { /* jamais de bruit */ }
    return false;
  }
}

async function chargerOutils() {
  const [garde, { auth }, { getFunctions, httpsCallable }] = await Promise.all([
    import("./authGuard"), import("./firebase"), import("firebase/functions"),
  ]);
  return {
    attendreAuthPrete: garde.attendreAuthPrete,
    signalerSiDefi: garde.signalerSiDefi,
    auth,
    appeler: (nom, data) => httpsCallable(getFunctions(undefined, "europe-west3"), nom)(data),
  };
}

/** Point d'entrée d'App.jsx : à chaque vue, sans attendre ni lever. */
export function noterVueModule({ view, session, lienPublic = false }) {
  const m = moduleANoter({ view, session, lienPublic });
  if (m) noterOuvertureModule(m, session).catch(() => {});
  return m;
}
